import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "components/layout/Header";
import Footer from "components/layout/Footer";
import {
  FileText,
  AlertTriangle,
  Bot,
  Scale,
  Globe,
  ShieldAlert,
  Copyright,
  Gavel,
  Ban,
  HandMetal,
  Sparkles,
  ArrowRight,
  ChevronDown,
  Info,
  Wrench,
  Eye,
  RefreshCw,
  Mail,
} from "lucide-react";

/* ═══════════════ ANIMATIONS ═══════════════ */

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

/* ═══════════════ TERMS DATA ═══════════════ */

const TERMS_SECTIONS = [
  {
    icon: Eye,
    title: "Acceptance of Terms (You're Already In)",
    emoji: "📜",
    content: `By visiting this website, you agree to these terms. Yes, just by being here. We didn't make the rules — actually, we did. That's literally what "Terms & Conditions" means.

If you don't agree with any part of these terms, your exit is the little X button on your browser tab. No hard feelings. But if you're still reading, welcome aboard. You're now legally bound to... well, just be reasonable. That's mostly what this document says in 2,000 words.`,
    gradient: "from-violet-500 to-purple-600",
    light: "bg-violet-50",
  },
  {
    icon: AlertTriangle,
    title: "The Data Might Be Made Up (Seriously)",
    emoji: "⚠️",
    content: `Here's where it gets real. Some of the statistics, numbers, percentages, and impressive-sounding figures on this website may be illustrative, estimated, projected, aspirational, or — let's just say it — fabricated for presentation purposes.

When we say "+340% increase in bookings" or "98% satisfaction rate," those numbers might be based on real data, or they might be the marketing team's best guess after three cups of chai. We try to be accurate, but this is a portfolio website, not a peer-reviewed scientific journal.

**The bottom line:** Don't make business decisions solely based on numbers you saw on our website. Don't cite us in your thesis. Don't bet your life savings on our conversion rates. Use common sense. If a number sounds too good to be true, it might be because someone got a little carried away with the copywriting.

We reserve the right to use approximations, round numbers, and the occasional creative liberty. If you need verified, audited data, please contact us directly and we'll give you the real (possibly less exciting) numbers.`,
    gradient: "from-amber-500 to-orange-600",
    light: "bg-amber-50",
  },
  {
    icon: Bot,
    title: "AI Disclosure (The Robot in the Room)",
    emoji: "🤖",
    content: `Let's address the elephant — or rather, the artificial intelligence — in the room.

This website was designed and developed with significant assistance from AI tools. This includes but is not limited to: code generation, content writing, design suggestions, animation logic, and yes, probably this very terms and conditions page. The irony is delicious.

**What this means for you:**
• The code was AI-assisted but human-reviewed and human-approved
• The content was AI-generated but edited, curated, and okayed by real people at BusiGrow  
• The design decisions were made by humans with AI as a tool, not the other way around
• No AI was harmed in the making of this website (they don't have feelings... yet)

**What this does NOT mean:**
• This is NOT a fully autonomous AI website that updates itself
• AI does NOT make business decisions for BusiGrow
• Your personal data is NOT being fed into AI models by us
• The chatbot (if present) is a tool, not a sentient being plotting world domination

We believe in transparency about AI use. Many companies use AI and don't tell you. We use AI and we're telling you right now, in a legal document, with excessive honesty.`,
    gradient: "from-emerald-500 to-teal-600",
    light: "bg-emerald-50",
  },
  {
    icon: Copyright,
    title: "Intellectual Property (Don't Copy Our Homework)",
    emoji: "©️",
    content: `Everything on this website — the design, the code, the text, the animations, the sarcastic tone — belongs to BusiGrow. Well, except for the stock photos (those belong to Unsplash photographers who are cooler than us) and the open-source libraries we used (shoutout to React, Tailwind, and Framer Motion).

You may NOT:
• Copy, reproduce, or redistribute our website content
• Pretend you made this website (please, we need the credit)
• Scrape our content for your own use
• Use our brand name, logo, or materials without written permission
• Screenshot our site and claim it's your portfolio piece

You MAY:
• Share links to our website (please do, actually)
• Reference us with proper attribution
• Be inspired by our design (flattery is welcome)
• Tell your friends how cool our website is (not legally required, but appreciated)`,
    gradient: "from-purple-500 to-indigo-600",
    light: "bg-purple-50",
  },
  {
    icon: Ban,
    title: "Prohibited Conduct (The Naughty List)",
    emoji: "🚫",
    content: `When using our website, you agree NOT to:

• Attempt to hack, breach, or compromise our systems (our security is decent, but please don't test it)
• Use automated tools to scrape, crawl, or harvest data from our website without permission
• Upload malicious code, viruses, or anything that makes computers cry
• Impersonate BusiGrow or any of our team members (we're flattered, but no)
• Use the website for any illegal purpose in any jurisdiction (we don't want to learn about obscure international cyber-laws through your arrest)
• Reverse-engineer our code (it's available in the browser anyway, but the principle stands)
• Spam our contact forms with AI-generated gibberish (ironic, we know)
• Use our content to train AI models without our consent (the circle of life)

Basically: don't be a jerk. If you're reading this wondering "but can I do [specific sketchy thing]?" — the answer is probably no.`,
    gradient: "from-red-500 to-rose-600",
    light: "bg-red-50",
  },
  {
    icon: ShieldAlert,
    title: "Limitation of Liability (The Legal Shield)",
    emoji: "🛡️",
    content: `BusiGrow, its founders, employees, contractors, AI assistants, and that one intern who made the coffee are NOT liable for:

• Any decisions you make based on information found on this website
• Loss of revenue, data, or sleep caused by visiting this website
• The existential crisis you may experience upon reading our AI disclosure
• Any inaccuracies in the data, statistics, or claims presented on this site
• Third-party websites we link to (we can't control the entire internet, though we'd like to try)
• Technical glitches, downtime, or the website acting weird at 3 AM
• Any indirect, incidental, special, or consequential damages

The website is provided "AS IS" and "AS AVAILABLE." We make no warranties, express or implied, that the website will be error-free, secure, or continuously available. It's a website, not a life support system.

Our total liability to you for any claims arising from your use of this website is limited to... well, the amount you paid to use it. Which is zero. So there's that.`,
    gradient: "from-slate-500 to-gray-600",
    light: "bg-slate-50",
  },
  {
    icon: Globe,
    title: "Governing Law (We Tried to Cover Everyone)",
    emoji: "🌏",
    content: `BusiGrow is headquartered in India. These terms are primarily governed by Indian law, including but not limited to:

• **Information Technology Act, 2000** (India)  
• **Digital Personal Data Protection Act (DPDPA), 2023** (India)
• **Consumer Protection Act, 2019** (India)

For our users in other regions, we also respect:
• **GDPR** — General Data Protection Regulation (European Union)
• **CCPA/CPRA** — California Consumer Privacy Act (United States)
• **PDPA** — Personal Data Protection Act (Singapore, Thailand)
• **POPIA** — Protection of Personal Information Act (South Africa)
• **PIPEDA** — Personal Information Protection (Canada)
• **UAE Data Protection Law** — Federal Decree-Law No. 45/2021 (UAE)
• **Saudi Arabia PDPL** — Personal Data Protection Law (KSA)

If you're from a country not listed above: we still respect your local data protection laws. We just couldn't list every country without this document becoming a geography textbook.

Any disputes will be resolved through arbitration in Mumbai, India. Unless you'd prefer to settle it over a friendly game of cricket. We're flexible.`,
    gradient: "from-indigo-500 to-blue-600",
    light: "bg-indigo-50",
  },
  {
    icon: Wrench,
    title: "Service Disclaimer (What We Actually Do)",
    emoji: "🔧",
    content: `BusiGrow provides branding, marketing, development, and AI services. The results shown on this website (case studies, portfolios, statistics) are meant to showcase our capabilities. They are NOT guarantees of specific outcomes for your project.

Every business is different. Your results will depend on your industry, budget, market conditions, how quickly you respond to our emails, and approximately 47 other variables we can't control.

We promise to give our best effort. We do NOT promise:
• That your revenue will increase by exactly 340%
• That our AI tools will solve all your problems
• That our designs will make you cry tears of joy (though they might)
• That reading our BusiMag articles will make you a business genius

Client testimonials and case studies represent individual experiences. Your mileage may vary. Void where prohibited. See your doctor if results last more than four hours. (That last one might be from a different disclaimer.)`,
    gradient: "from-fuchsia-500 to-pink-600",
    light: "bg-fuchsia-50",
  },
  {
    icon: RefreshCw,
    title: "Changes to These Terms (The Never-Ending Story)",
    emoji: "🔄",
    content: `We reserve the right to modify these terms at any time, for any reason, with or without notice. We'll try to let you know about significant changes, but honestly, how many people check back for terms updates? Exactly.

The "Last Updated" date at the top of this page will change when we update these terms. If a change is significant (like we suddenly decide to claim ownership of your firstborn), we'll make a reasonable effort to notify you.

Your continued use of the website after changes are posted constitutes acceptance of the new terms. If you disagree with the changes, please refer back to the exit instructions in Section 1 (the X button on your browser tab).`,
    gradient: "from-violet-500 to-fuchsia-600",
    light: "bg-violet-50",
  },
  {
    icon: Mail,
    title: "Contact Us (We're Real Humans. Mostly.)",
    emoji: "📬",
    content: `If you have questions about these terms, or if you just want to tell us that you actually read all of this (we'll be genuinely impressed), you can reach us at:

**BusiGrow**
📧 Email: hello@busigrow.in
🌐 Website: busigrow.in
📍 Location: Mumbai, India (and digitally everywhere)

Response time: We try to get back within 24-48 hours. If it takes longer, we're either very busy or the AI is having an existential crisis. Either way, we'll respond eventually.

For legal inquiries, please use the subject line "Legal" so we know to put on our serious faces before reading your email.`,
    gradient: "from-teal-500 to-cyan-600",
    light: "bg-teal-50",
  },
];

/* ═══════════════ SECTION COMPONENT ═══════════════ */

function TermsSection({ section, index }) {
  const [expanded, setExpanded] = useState(true);
  const Icon = section.icon;

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      variants={fadeUp}
      className="group"
    >
      <motion.div
        whileHover={{ y: -4, scale: 1.005 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="card-shine glass-strong rounded-3xl p-6 sm:p-8 shadow-apple hover:shadow-apple-xl transition-all duration-500"
      >
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-start sm:items-center gap-4 text-left"
        >
          <div className={`shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br ${section.gradient} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-2xl">{section.emoji}</span>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                {section.title}
              </h3>
            </div>
            <p className="text-xs text-slate-400 mt-1">Section {index + 1} of {TERMS_SECTIONS.length}</p>
          </div>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="shrink-0 w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center"
          >
            <ChevronDown className="w-4 h-4 text-violet-500" />
          </motion.div>
        </button>

        {/* Content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="prose prose-slate prose-sm max-w-none text-slate-600 leading-relaxed whitespace-pre-line">
                  {section.content.split("**").map((part, i) =>
                    i % 2 === 1 ? (
                      <strong key={i} className="text-slate-800 font-semibold">{part}</strong>
                    ) : (
                      <span key={i}>{part}</span>
                    )
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ HERO ═══════════════ */

function TermsHero() {
  return (
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-24 hero-mesh">
      {/* Background decorations */}
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-purple-200/30 animate-morph-blob" />
      <div className="absolute bottom-0 -right-20 w-60 h-60 rounded-full bg-violet-200/20 animate-morph-blob" style={{ animationDelay: "-5s" }} />
      <div className="absolute top-20 left-1/4 w-16 h-16 rounded-full bg-amber-200/40 animate-float-orb" />
      <div className="absolute bottom-10 right-1/4 w-10 h-10 rounded-full bg-violet-200/40 animate-float-orb" style={{ animationDelay: "2s" }} />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          {/* Badge */}
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-6">
            <Gavel className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-medium text-slate-600">Last Updated: March 2026</span>
          </motion.div>

          {/* Title */}
          <motion.h1
            variants={fadeUp}
            className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6"
          >
            <span className="text-slate-900">Terms & Conditions</span>
            <br />
            <span className="text-gradient-vibrant">(You Won't Read This Either)</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p variants={fadeUp} className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed mb-4">
            The legal stuff that protects us from... well, everything. Written in English that actual humans can understand, with a generous helping of sarcasm.
          </motion.p>

          <motion.p variants={fadeUp} className="text-sm text-slate-400 max-w-xl mx-auto">
            TL;DR — Don't steal our stuff, our numbers might be approximate, we used AI to build this, and we're legally compliant in more countries than we can count. The long version is below.
          </motion.p>

          {/* Warning card */}
          <motion.div
            variants={fadeUp}
            className="mt-8 inline-flex items-center gap-3 glass-strong rounded-2xl px-6 py-3 shadow-apple border border-amber-200/50"
          >
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-medium text-slate-700">Contains: Legal jargon, bad jokes, and radical honesty</span>
            <span className="text-lg">⚖️</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════ QUICK SUMMARY BAR ═══════════════ */

function QuickSummary() {
  const highlights = [
    { icon: AlertTriangle, label: "Data may be illustrative", color: "text-amber-500", bg: "bg-amber-50" },
    { icon: Bot, label: "AI-assisted development", color: "text-emerald-500", bg: "bg-emerald-50" },
    { icon: Copyright, label: "All rights reserved", color: "text-violet-500", bg: "bg-violet-50" },
    { icon: Globe, label: "Globally compliant", color: "text-blue-500", bg: "bg-blue-50" },
  ];

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={stagger}
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-12 relative z-20"
    >
      <motion.div variants={fadeUp} className="glass-strong rounded-2xl p-4 shadow-apple-lg">
        <p className="text-xs text-slate-400 text-center mb-3 font-medium uppercase tracking-wider">Quick Summary</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {highlights.map((h, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -2, scale: 1.03 }}
              className={`flex items-center gap-2 ${h.bg} rounded-xl px-3 py-2.5`}
            >
              <h.icon className={`w-4 h-4 ${h.color} shrink-0`} />
              <span className="text-xs font-medium text-slate-700">{h.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ MAIN PAGE ═══════════════ */

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
      <Header />

      <TermsHero />
      <QuickSummary />

      {/* Terms Sections */}
      <section className="relative py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {TERMS_SECTIONS.map((section, i) => (
              <TermsSection key={i} section={section} index={i} />
            ))}
          </div>

          {/* Final note */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-16 text-center"
          >
            <div className="glass-strong rounded-3xl p-8 shadow-apple card-shine">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg animate-glow-pulse">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">You Made It to the End 🎉</h3>
              <p className="text-slate-500 max-w-lg mx-auto text-sm leading-relaxed mb-2">
                Congratulations on being the kind of person who reads Terms & Conditions. 
                You're either very responsible, very bored, or a lawyer. Either way, we appreciate you.
              </p>
              <p className="text-slate-400 max-w-lg mx-auto text-xs leading-relaxed mb-6">
                Remember: this website was made with love, caffeine, AI tools, and an unreasonable amount of CSS animations. 
                The data is illustrative, the jokes are intentional, and the legal protection is very real.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/privacy"
                  className="inline-flex items-center gap-2 px-6 py-3 glass font-semibold text-slate-700 text-sm rounded-2xl hover:bg-white/70 transition-all duration-300"
                >
                  Read Privacy Policy
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-sm rounded-2xl shadow-apple-lg hover:shadow-apple-xl hover:scale-[1.02] transition-all duration-300"
                >
                  Got Questions? Contact Us
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-8 pt-6 border-t border-slate-100">
                <p className="text-[10px] text-slate-300 max-w-2xl mx-auto leading-relaxed">
                  These Terms & Conditions are effective as of March 2026. BusiGrow is compliant with the 
                  Information Technology Act 2000 (India), DPDPA 2023 (India), Consumer Protection Act 2019 (India), 
                  GDPR (EU), CCPA/CPRA (California, USA), PDPA (Singapore & Thailand), POPIA (South Africa), 
                  PIPEDA (Canada), UAE Federal Decree-Law No. 45/2021, Saudi Arabia PDPL, and applicable data protection 
                  laws in all jurisdictions where BusiGrow operates. This website is provided "as is" without warranties 
                  of any kind. All content, statistics, and case studies are illustrative unless explicitly stated otherwise. 
                  AI tools were used in the creation of this website. © 2026 BusiGrow. All rights reserved.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
